// ---------------------------------------------------------------------------
// realtime-store.ts — Client-side state manager for realtime apps.
//
// Manages: WS connection, in-memory state, IDB persistence, optimistic
// writes, delta accumulation, loading states, ephemeral/durable tiers.
// ---------------------------------------------------------------------------
import { getConfig, isBoundedNetwork } from './config';
// ---------------------------------------------------------------------------
// IDB helpers (lazy-loaded, non-blocking)
// ---------------------------------------------------------------------------
const IDB_NAME = 'bounded-realtime';
const IDB_STORE = 'subscriptions';
const IDB_VERSION = 1;
let idbPromise = null;
function getIDB() {
    if (idbPromise)
        return idbPromise;
    if (typeof indexedDB === 'undefined') {
        return Promise.reject(new Error('IndexedDB not available'));
    }
    idbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return idbPromise;
}
async function idbGet(key) {
    try {
        const db = await getIDB();
        return new Promise((resolve) => {
            const tx = db.transaction(IDB_STORE, 'readonly');
            const store = tx.objectStore(IDB_STORE);
            const req = store.get(key);
            req.onsuccess = () => { var _a; return resolve((_a = req.result) !== null && _a !== void 0 ? _a : null); };
            req.onerror = () => resolve(null);
        });
    }
    catch (_a) {
        return null;
    }
}
async function idbSet(key, value) {
    try {
        const db = await getIDB();
        return new Promise((resolve) => {
            const tx = db.transaction(IDB_STORE, 'readwrite');
            const store = tx.objectStore(IDB_STORE);
            store.put(value, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        });
    }
    catch (_a) {
        // Best-effort persistence
    }
}
async function idbDelete(key) {
    try {
        const db = await getIDB();
        return new Promise((resolve) => {
            const tx = db.transaction(IDB_STORE, 'readwrite');
            const store = tx.objectStore(IDB_STORE);
            store.delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        });
    }
    catch (_a) {
        // Best-effort
    }
}
// ---------------------------------------------------------------------------
// RealtimeStore
// ---------------------------------------------------------------------------
let nextRequestId = 1;
function hashForKey(value) {
    let h = 5381;
    for (let i = 0; i < value.length; i++) {
        h = ((h << 5) + h + value.charCodeAt(i)) & 0x7fffffff;
    }
    return h.toString(36);
}
function principalFromToken(token) {
    return token ? `t${hashForKey(token)}` : 'anon';
}
export class RealtimeStore {
    constructor() {
        this.ws = null;
        this.wsUrl = '';
        this.appId = '';
        this.subscriptions = new Map();
        this.pendingRequests = new Map();
        this.connectPromise = null;
        this.reconnectTimer = null;
        this.reconnectDelay = 1000;
        this.maxReconnectDelay = 30000;
        this.idbFlushTimer = null;
        this.idbDirtyKeys = new Set();
        this.closed = false;
        this.authToken = null;
        this.authPrincipalKey = 'anon';
        this.authenticating = false;
        this.suppressNextReconnect = false;
        this.isServer = false;
        this.tokenRefreshTimer = null;
        // -----------------------------------------------------------------------
        // WebSocket connection
        // -----------------------------------------------------------------------
        this.initPromise = null;
    }
    // -----------------------------------------------------------------------
    // Initialization
    // -----------------------------------------------------------------------
    async init() {
        const config = await getConfig();
        this.appId = config.appId;
        this.wsUrl = config.wsApiUrl;
        this.isServer = config.isServer;
        await this.refreshToken();
        this.startTokenRefresh();
    }
    async refreshToken() {
        let token = null;
        try {
            const { getIdToken } = await import('../utils/utils');
            token = await getIdToken(this.isServer);
        }
        catch ( /* no auth available */_a) { /* no auth available */ }
        this.authToken = token !== null && token !== void 0 ? token : null;
        this.authPrincipalKey = principalFromToken(this.authToken);
    }
    startTokenRefresh() {
        if (this.tokenRefreshTimer)
            return;
        this.tokenRefreshTimer = setInterval(async () => {
            const prevPrincipal = this.authPrincipalKey;
            await this.refreshToken();
            if (this.authPrincipalKey !== prevPrincipal) {
                await this.applyAuthPrincipalChange();
                if (this.subscriptions.size > 0) {
                    await this.ensureConnected().catch(() => {
                        this.setAllSubscriptionStatus('error');
                    });
                }
            }
        }, 5 * 60 * 1000); // Check every 5 minutes
    }
    async ensureInitialized() {
        if (this.appId)
            return;
        if (!this.initPromise)
            this.initPromise = this.init();
        await this.initPromise;
    }
    async ensureCurrentAuth() {
        await this.ensureInitialized();
        const prevPrincipal = this.authPrincipalKey;
        await this.refreshToken();
        if (this.authPrincipalKey !== prevPrincipal) {
            await this.applyAuthPrincipalChange();
        }
    }
    rekeySubscriptionsForPrincipal() {
        const subs = Array.from(this.subscriptions.values());
        this.subscriptions.clear();
        for (const sub of subs) {
            this.subscriptions.set(this.getSubKey(sub.path, sub.options), sub);
        }
    }
    async applyAuthPrincipalChange() {
        if (this.idbFlushTimer) {
            clearTimeout(this.idbFlushTimer);
            this.idbFlushTimer = null;
        }
        this.idbDirtyKeys.clear();
        this.rekeySubscriptionsForPrincipal();
        for (const sub of this.subscriptions.values()) {
            sub.docs.clear();
            sub.ref.current = sub.docs;
            sub.error = null;
            sub.isStale = false;
            let loaded = false;
            if (this.shouldUseIdb(sub.tier, sub.options)) {
                const cached = await idbGet(this.idbKey(sub.path));
                if (cached && Array.isArray(cached)) {
                    for (const doc of cached) {
                        if (doc && doc._id)
                            sub.docs.set(doc._id, doc);
                    }
                    sub.ref.current = sub.docs;
                    loaded = sub.docs.size > 0;
                }
            }
            sub.status = loaded ? 'cached' : 'loading';
            sub.isStale = loaded;
            if (loaded)
                this.notifySubscription(sub);
            else
                this.notifyState(sub);
        }
        if (this.ws) {
            const ws = this.ws;
            this.ws = null;
            this.connectPromise = null;
            this.suppressNextReconnect = true;
            try {
                ws.close(1000, 'Auth changed');
            }
            catch ( /* ignore */_a) { /* ignore */ }
        }
    }
    async ensureConnected() {
        var _a;
        await this.ensureCurrentAuth();
        if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN && !this.authenticating)
            return;
        if (this.connectPromise)
            return this.connectPromise;
        this.connectPromise = this.connect();
        return this.connectPromise;
    }
    connect() {
        return new Promise((resolve, reject) => {
            if (this.closed) {
                reject(new Error('Store closed'));
                return;
            }
            const params = new URLSearchParams();
            params.set('appId', this.appId);
            const url = `${this.wsUrl}?${params.toString()}`;
            const ws = new WebSocket(url);
            this.ws = ws;
            let authTimer = null;
            const finishConnected = () => {
                if (authTimer) {
                    clearTimeout(authTimer);
                    authTimer = null;
                }
                this.authenticating = false;
                ws.removeEventListener('error', onError);
                this.reconnectDelay = 1000;
                this.connectPromise = null;
                this.resubscribeAll();
                resolve();
            };
            const onOpen = () => {
                if (!this.authToken) {
                    finishConnected();
                    return;
                }
                this.authenticating = true;
                authTimer = setTimeout(() => {
                    this.authenticating = false;
                    this.connectPromise = null;
                    try {
                        ws.close(1008, 'Authentication timeout');
                    }
                    catch ( /* ignore */_a) { /* ignore */ }
                    reject(new Error('WebSocket authentication timeout'));
                }, 10000);
                try {
                    ws.send(JSON.stringify({ type: 'auth', token: this.authToken }));
                }
                catch (e) {
                    if (authTimer)
                        clearTimeout(authTimer);
                    this.authenticating = false;
                    this.connectPromise = null;
                    reject(e);
                }
            };
            const onError = (e) => {
                if (authTimer)
                    clearTimeout(authTimer);
                this.authenticating = false;
                ws.removeEventListener('open', onOpen);
                this.connectPromise = null;
                reject(new Error('WebSocket connection failed'));
            };
            ws.addEventListener('open', onOpen, { once: true });
            ws.addEventListener('error', onError, { once: true });
            ws.addEventListener('message', (event) => {
                if (this.authenticating) {
                    try {
                        const msg = JSON.parse(typeof event.data === 'string' ? event.data : new TextDecoder().decode(event.data));
                        if ((msg === null || msg === void 0 ? void 0 : msg.type) === 'authenticated') {
                            finishConnected();
                            return;
                        }
                    }
                    catch ( /* fall through to normal handling */_a) { /* fall through to normal handling */ }
                }
                this.handleMessage(event.data);
            });
            ws.addEventListener('close', () => {
                if (authTimer)
                    clearTimeout(authTimer);
                if (this.ws !== ws) {
                    if (this.suppressNextReconnect)
                        this.suppressNextReconnect = false;
                    return;
                }
                this.authenticating = false;
                this.ws = null;
                this.connectPromise = null;
                this.rejectAllPending('WebSocket closed');
                this.setAllSubscriptionStatus('reconnecting');
                if (this.suppressNextReconnect) {
                    this.suppressNextReconnect = false;
                    return;
                }
                this.scheduleReconnect();
            });
        });
    }
    scheduleReconnect() {
        if (this.closed)
            return;
        if (this.reconnectTimer)
            clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => {
            this.ensureConnected().catch(() => {
                this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
                this.scheduleReconnect();
            });
        }, this.reconnectDelay);
    }
    resubscribeAll() {
        for (const sub of this.subscriptions.values()) {
            this.sendSubscribe(sub);
        }
    }
    // -----------------------------------------------------------------------
    // Message handling
    // -----------------------------------------------------------------------
    handleMessage(raw) {
        const text = typeof raw === 'string' ? raw : new TextDecoder().decode(raw);
        let msg;
        try {
            msg = JSON.parse(text);
        }
        catch (_a) {
            return;
        }
        switch (msg.type) {
            case 'snapshot':
                this.handleSnapshot(msg);
                break;
            case 'delta':
                this.handleDelta(msg);
                break;
            case 'result':
                this.handleResult(msg);
                break;
            case 'error':
                this.handleError(msg);
                break;
            case 'pong':
                break;
            case 'authenticated':
                break;
            // v1 compat: handle legacy message types during transition
            case 'subscribed':
                this.handleSnapshot(Object.assign(Object.assign({}, msg), { type: 'snapshot', docs: msg.data }));
                break;
            case 'data':
                // Legacy full-snapshot delta — treat as snapshot replacement
                this.handleLegacyData(msg);
                break;
            case 'response':
                this.handleResult(Object.assign(Object.assign({}, msg), { type: 'result', ok: msg.status === 200, doc: msg.data }));
                break;
        }
    }
    handleSnapshot(msg) {
        var _a, _b, _c;
        const subId = (_a = msg.id) !== null && _a !== void 0 ? _a : msg.subscriptionId;
        if (!subId)
            return;
        const sub = this.findSubscriptionById(subId);
        if (!sub)
            return;
        const docs = (_c = (_b = msg.docs) !== null && _b !== void 0 ? _b : msg.data) !== null && _c !== void 0 ? _c : [];
        const docsArray = Array.isArray(docs) ? docs : [docs];
        sub.docs.clear();
        for (const doc of docsArray) {
            if (doc && doc._id) {
                sub.docs.set(doc._id, doc);
            }
        }
        sub.ref.current = sub.docs;
        sub.status = 'live';
        sub.isStale = false;
        sub.error = null;
        this.notifySubscription(sub);
        this.markIdbDirty(sub.path);
    }
    handleDelta(msg) {
        var _a, _b;
        const subId = (_a = msg.id) !== null && _a !== void 0 ? _a : msg.subscriptionId;
        if (!subId)
            return;
        const sub = this.findSubscriptionById(subId);
        if (!sub)
            return;
        if (sub.tier === 'ephemeral') {
            // Ephemeral: just overwrite, no accumulation logic
            if (msg.change === 'removed' && msg.docId) {
                sub.docs.delete(msg.docId);
            }
            else if (msg.doc && msg.doc._id) {
                sub.docs.set(msg.doc._id, msg.doc);
            }
            sub.ref.current = sub.docs;
            if (sub.options.mode !== 'ref') {
                this.notifySubscription(sub);
            }
            return;
        }
        // Durable/checkpointed: full delta handling
        switch (msg.change) {
            case 'added':
            case 'modified':
                if (msg.doc && msg.doc._id) {
                    sub.docs.set(msg.doc._id, msg.doc);
                }
                break;
            case 'removed':
                if (msg.docId) {
                    sub.docs.delete(msg.docId);
                }
                else if ((_b = msg.doc) === null || _b === void 0 ? void 0 : _b._id) {
                    sub.docs.delete(msg.doc._id);
                }
                break;
        }
        sub.ref.current = sub.docs;
        this.notifySubscription(sub);
        this.markIdbDirty(sub.path);
    }
    handleLegacyData(msg) {
        // Legacy v1 format: 'data' message with full snapshot or single doc
        const subId = msg.subscriptionId;
        if (!subId)
            return;
        const sub = this.findSubscriptionById(subId);
        if (!sub)
            return;
        if (Array.isArray(msg.data)) {
            // Full snapshot replacement
            sub.docs.clear();
            for (const doc of msg.data) {
                if (doc && doc._id)
                    sub.docs.set(doc._id, doc);
            }
        }
        else if (msg.data && msg.data._id) {
            // Single doc update
            sub.docs.set(msg.data._id, msg.data);
        }
        else if (msg.data === null) {
            // Removal — but we don't know which doc. Re-fetch needed.
        }
        sub.ref.current = sub.docs;
        sub.status = 'live';
        sub.isStale = false;
        this.notifySubscription(sub);
        this.markIdbDirty(sub.path);
    }
    handleResult(msg) {
        var _a, _b, _c, _d;
        const requestId = msg.requestId;
        if (!requestId)
            return;
        const pending = this.pendingRequests.get(requestId);
        if (!pending)
            return;
        this.pendingRequests.delete(requestId);
        clearTimeout(pending.timeout);
        const ok = (_a = msg.ok) !== null && _a !== void 0 ? _a : (msg.status === 200);
        if (ok) {
            pending.resolve((_c = (_b = msg.doc) !== null && _b !== void 0 ? _b : msg.data) !== null && _c !== void 0 ? _c : true);
        }
        else {
            pending.reject(new Error((_d = msg.error) !== null && _d !== void 0 ? _d : 'Operation failed'));
        }
    }
    handleError(msg) {
        var _a, _b, _c;
        const error = new Error((_a = msg.message) !== null && _a !== void 0 ? _a : (msg.code ? `${msg.code}: Server error` : 'Server error'));
        if (msg.code)
            error.code = msg.code;
        if (msg.subscriptionId || msg.id)
            error.subscriptionId = (_b = msg.subscriptionId) !== null && _b !== void 0 ? _b : msg.id;
        const requestId = msg.requestId;
        if (requestId) {
            const pending = this.pendingRequests.get(requestId);
            if (pending) {
                this.pendingRequests.delete(requestId);
                clearTimeout(pending.timeout);
                pending.reject(error);
            }
        }
        const subId = (_c = msg.subscriptionId) !== null && _c !== void 0 ? _c : msg.id;
        if (subId) {
            const sub = this.findSubscriptionById(subId);
            if (sub) {
                sub.status = 'error';
                sub.error = error;
                this.notifyState(sub);
                for (const callback of Array.from(sub.errorCallbacks)) {
                    try {
                        callback(error);
                    }
                    catch ( /* swallow */_d) { /* swallow */ }
                }
            }
        }
    }
    // -----------------------------------------------------------------------
    // Subscribe
    // -----------------------------------------------------------------------
    async subscribe(path, opts = {}) {
        var _a;
        await this.ensureCurrentAuth();
        const tier = (_a = opts.tier) !== null && _a !== void 0 ? _a : 'durable';
        const subKey = this.getSubKey(path, opts);
        let sub = this.subscriptions.get(subKey);
        if (sub) {
            // Existing subscription — add callback
            if (opts.onData)
                sub.callbacks.add(opts.onData);
            if (opts.onState)
                sub.stateCallbacks.add(opts.onState);
            if (opts.onError)
                sub.errorCallbacks.add(opts.onError);
            if (opts.cache && !sub.options.cache) {
                sub.options = Object.assign(Object.assign({}, sub.options), { cache: true });
            }
            // Immediately deliver current state
            if (opts.onData && sub.docs.size > 0) {
                opts.onData(this.docsToArray(sub));
            }
            if (opts.onState) {
                opts.onState(this.getState(sub));
            }
            return this.createUnsubscribe(subKey, sub.id, opts.onData, opts.onState, opts.onError);
        }
        // New subscription
        const subId = `sub_${nextRequestId++}`;
        sub = {
            id: subId,
            path,
            tier,
            options: opts,
            docs: new Map(),
            status: 'idle',
            isStale: false,
            error: null,
            callbacks: new Set(opts.onData ? [opts.onData] : []),
            stateCallbacks: new Set(opts.onState ? [opts.onState] : []),
            errorCallbacks: new Set(opts.onError ? [opts.onError] : []),
            ref: { current: new Map() },
        };
        this.subscriptions.set(subKey, sub);
        // Step 1: Load from IDB only when explicitly enabled and principal-bound.
        if (this.shouldUseIdb(tier, opts)) {
            const cached = await idbGet(this.idbKey(path));
            if (cached && Array.isArray(cached)) {
                for (const doc of cached) {
                    if (doc && doc._id)
                        sub.docs.set(doc._id, doc);
                }
                sub.ref.current = sub.docs;
                sub.status = 'cached';
                sub.isStale = true;
                this.notifySubscription(sub);
            }
        }
        // Step 2: Connect and subscribe via WS
        sub.status = sub.docs.size > 0 ? 'cached' : 'loading';
        this.notifyState(sub);
        try {
            await this.ensureConnected();
            this.sendSubscribe(sub);
        }
        catch (_b) {
            sub.status = 'error';
            sub.error = new Error('Connection failed');
            this.notifyState(sub);
        }
        return this.createUnsubscribe(subKey, sub.id, opts.onData, opts.onState, opts.onError);
    }
    getRef(path, opts = {}) {
        var _a;
        const subKey = this.getSubKey(path, opts);
        const sub = this.subscriptions.get(subKey);
        if (sub)
            return sub.ref;
        // Auto-subscribe in ref mode
        const ref = { current: new Map() };
        this.subscribe(path, Object.assign(Object.assign({}, opts), { mode: 'ref', tier: 'ephemeral' })).catch(() => { });
        const newSub = this.subscriptions.get(this.getSubKey(path, Object.assign(Object.assign({}, opts), { tier: 'ephemeral' })));
        return (_a = newSub === null || newSub === void 0 ? void 0 : newSub.ref) !== null && _a !== void 0 ? _a : ref;
    }
    // -----------------------------------------------------------------------
    // CRUD operations
    // -----------------------------------------------------------------------
    async set(path, doc) {
        var _a;
        await this.ensureConnected();
        // Resolve operations (Increment, Time.Now) client-side for optimistic update
        const resolvedDoc = this.resolveOperations(doc, path);
        // Optimistic update: apply to local state immediately
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        const collectionPath = this.getCollectionPath(normalizedPath);
        const optimisticDoc = Object.assign(Object.assign({ _id: normalizedPath, pathId: normalizedPath }, resolvedDoc), { 
            // System timestamp field name: the Bounded worker stamps the neutral
            // `_updatedAt`; the underscore-prefixed `_updated_at` metadata mirror.
            // Match it so the optimistic doc lines up with the server's confirmation.
            [isBoundedNetwork() ? '_updatedAt' : '_updated_at']: Date.now() });
        const sub = this.findSubscriptionByPath(collectionPath);
        let prevDoc = null;
        if (sub) {
            prevDoc = (_a = sub.docs.get(normalizedPath)) !== null && _a !== void 0 ? _a : null;
            sub.docs.set(normalizedPath, optimisticDoc);
            sub.ref.current = sub.docs;
            this.notifySubscription(sub);
        }
        // Send to server
        const requestId = `r_${nextRequestId++}`;
        try {
            const result = await this.sendRequest(requestId, {
                type: 'set',
                requestId,
                documents: [{ destinationPath: normalizedPath, document: doc }],
            });
            // Replace optimistic doc with server-confirmed version
            if (sub && result && typeof result === 'object') {
                const serverDoc = Array.isArray(result) ? result[0] : result;
                if (serverDoc && serverDoc._id) {
                    sub.docs.set(serverDoc._id, serverDoc);
                    sub.ref.current = sub.docs;
                    this.notifySubscription(sub);
                    this.markIdbDirty(collectionPath);
                }
            }
            return Array.isArray(result) ? result[0] : result;
        }
        catch (err) {
            // Revert optimistic update
            if (sub) {
                if (prevDoc) {
                    sub.docs.set(normalizedPath, prevDoc);
                }
                else {
                    sub.docs.delete(normalizedPath);
                }
                sub.ref.current = sub.docs;
                this.notifySubscription(sub);
            }
            throw err;
        }
    }
    async get(path) {
        await this.ensureCurrentAuth();
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        // Check local subscriptions first
        const collectionPath = this.getCollectionPath(normalizedPath);
        const sub = this.findSubscriptionByPath(collectionPath);
        if (sub && sub.status === 'live') {
            const doc = sub.docs.get(normalizedPath);
            return doc !== null && doc !== void 0 ? doc : null;
        }
        // One-shot WS fetch
        await this.ensureConnected();
        const requestId = `r_${nextRequestId++}`;
        return this.sendRequest(requestId, {
            type: 'get',
            requestId,
            path: normalizedPath,
        });
    }
    async getMany(paths) {
        await this.ensureConnected();
        const normalizedPaths = paths.map(p => p.startsWith('/') ? p.slice(1) : p);
        const requestId = `r_${nextRequestId++}`;
        return this.sendRequest(requestId, {
            type: 'getMany',
            requestId,
            paths: normalizedPaths,
        });
    }
    async delete(path) {
        var _a;
        await this.ensureConnected();
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        // Optimistic: remove from local state
        const collectionPath = this.getCollectionPath(normalizedPath);
        const sub = this.findSubscriptionByPath(collectionPath);
        let prevDoc = null;
        if (sub) {
            prevDoc = (_a = sub.docs.get(normalizedPath)) !== null && _a !== void 0 ? _a : null;
            sub.docs.delete(normalizedPath);
            sub.ref.current = sub.docs;
            this.notifySubscription(sub);
        }
        const requestId = `r_${nextRequestId++}`;
        try {
            await this.sendRequest(requestId, {
                type: 'delete',
                requestId,
                path: normalizedPath,
            });
            if (sub)
                this.markIdbDirty(collectionPath);
        }
        catch (err) {
            // Revert
            if (sub && prevDoc) {
                sub.docs.set(normalizedPath, prevDoc);
                sub.ref.current = sub.docs;
                this.notifySubscription(sub);
            }
            throw err;
        }
    }
    async query(path, opts) {
        await this.ensureConnected();
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        const requestId = `r_${nextRequestId++}`;
        return this.sendRequest(requestId, Object.assign(Object.assign(Object.assign(Object.assign({ type: 'query', requestId, path: normalizedPath }, ((opts === null || opts === void 0 ? void 0 : opts.filter) ? { filter: opts.filter } : {})), ((opts === null || opts === void 0 ? void 0 : opts.sort) ? { sort: opts.sort } : {})), ((opts === null || opts === void 0 ? void 0 : opts.limit) !== undefined ? { limit: opts.limit } : {})), ((opts === null || opts === void 0 ? void 0 : opts.includeSubPaths) ? { includeSubPaths: true } : {})));
    }
    async count(path) {
        var _a;
        await this.ensureConnected();
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        const requestId = `r_${nextRequestId++}`;
        const result = await this.sendRequest(requestId, {
            type: 'count',
            requestId,
            path: normalizedPath,
        });
        return typeof result === 'number' ? result : ((_a = result === null || result === void 0 ? void 0 : result.value) !== null && _a !== void 0 ? _a : 0);
    }
    async aggregate(path, operation, opts) {
        await this.ensureConnected();
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        const requestId = `r_${nextRequestId++}`;
        return this.sendRequest(requestId, Object.assign({ type: 'aggregate', requestId, path: normalizedPath, operation }, ((opts === null || opts === void 0 ? void 0 : opts.field) ? { field: opts.field } : {})));
    }
    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------
    sendSubscribe(sub) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
            return;
        const msg = {
            type: 'subscribe',
            subscriptionId: sub.id,
            path: sub.path,
        };
        if (sub.options.filter)
            msg.filter = sub.options.filter;
        if (sub.options.includeSubPaths)
            msg.includeSubPaths = true;
        if (sub.options.limit)
            msg.limit = sub.options.limit;
        if (sub.options.prompt)
            msg.prompt = sub.options.prompt;
        this.ws.send(JSON.stringify(msg));
    }
    sendRequest(requestId, msg) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(requestId);
                reject(new Error('Request timed out'));
            }, 30000);
            this.pendingRequests.set(requestId, { resolve, reject, timeout });
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify(msg));
            }
            else {
                this.pendingRequests.delete(requestId);
                clearTimeout(timeout);
                reject(new Error('WebSocket not connected'));
            }
        });
    }
    notifySubscription(sub) {
        const data = this.docsToArray(sub);
        const callbacks = Array.from(sub.callbacks);
        for (const cb of callbacks) {
            try {
                cb(data);
            }
            catch ( /* swallow callback errors */_a) { /* swallow callback errors */ }
        }
        this.notifyState(sub);
    }
    notifyState(sub) {
        const state = this.getState(sub);
        const callbacks = Array.from(sub.stateCallbacks);
        for (const cb of callbacks) {
            try {
                cb(state);
            }
            catch ( /* swallow */_a) { /* swallow */ }
        }
    }
    getState(sub) {
        return {
            data: this.docsToArray(sub),
            status: sub.status,
            isStale: sub.isStale,
            error: sub.error,
        };
    }
    docsToArray(sub) {
        return Array.from(sub.docs.values());
    }
    findSubscriptionById(id) {
        for (const sub of this.subscriptions.values()) {
            if (sub.id === id)
                return sub;
        }
        return undefined;
    }
    findSubscriptionByPath(collectionPath) {
        for (const sub of this.subscriptions.values()) {
            const subPath = sub.path.startsWith('/') ? sub.path.slice(1) : sub.path;
            if (subPath === collectionPath)
                return sub;
            if (collectionPath.startsWith(subPath + '/'))
                return sub;
        }
        return undefined;
    }
    getCollectionPath(docPath) {
        const segments = docPath.split('/');
        if (segments.length % 2 === 0) {
            return segments.slice(0, -1).join('/');
        }
        return docPath;
    }
    getSubKey(path, opts) {
        const parts = [this.appId, this.authPrincipalKey, path];
        if (opts.filter)
            parts.push(JSON.stringify(opts.filter));
        if (opts.prompt)
            parts.push(opts.prompt);
        if (opts.tier)
            parts.push(opts.tier);
        return parts.join('::');
    }
    idbKey(path) {
        return `${this.appId}:${this.authPrincipalKey}:${path}`;
    }
    shouldUseIdb(tier, opts) {
        return opts.cache === true && tier !== 'ephemeral' && this.authPrincipalKey !== 'anon';
    }
    markIdbDirty(path) {
        const sub = this.findSubscriptionByPath(path);
        if (!sub || !this.shouldUseIdb(sub.tier, sub.options))
            return;
        this.idbDirtyKeys.add(path);
        if (!this.idbFlushTimer) {
            this.idbFlushTimer = setTimeout(() => {
                this.flushIdb();
                this.idbFlushTimer = null;
            }, 500);
        }
    }
    async flushIdb() {
        const keys = Array.from(this.idbDirtyKeys);
        this.idbDirtyKeys.clear();
        for (const path of keys) {
            const sub = this.findSubscriptionByPath(path);
            if (sub && this.shouldUseIdb(sub.tier, sub.options)) {
                const docs = this.docsToArray(sub);
                await idbSet(this.idbKey(path), docs);
            }
        }
    }
    createUnsubscribe(subKey, subId, onData, onState, onError) {
        return async () => {
            var _a;
            const sub = (_a = this.subscriptions.get(subKey)) !== null && _a !== void 0 ? _a : this.findSubscriptionById(subId);
            if (!sub)
                return;
            const currentSubKey = this.getSubKey(sub.path, sub.options);
            if (onData)
                sub.callbacks.delete(onData);
            if (onState)
                sub.stateCallbacks.delete(onState);
            if (onError)
                sub.errorCallbacks.delete(onError);
            // If no more callbacks, unsubscribe entirely
            if (sub.callbacks.size === 0 && sub.stateCallbacks.size === 0 && sub.errorCallbacks.size === 0) {
                this.subscriptions.delete(subKey);
                this.subscriptions.delete(currentSubKey);
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify({
                        type: 'unsubscribe',
                        subscriptionId: sub.id,
                    }));
                }
            }
        };
    }
    resolveOperations(doc, path) {
        var _a;
        if (!doc || typeof doc !== 'object')
            return doc;
        const resolved = {};
        for (const [key, value] of Object.entries(doc)) {
            if (value && typeof value === 'object' && !Array.isArray(value) && value.operation) {
                const op = value;
                if (op.operation === 'time' && op.value === 'now') {
                    resolved[key] = Math.floor(Date.now() / 1000);
                }
                else if (op.operation === 'increment') {
                    // For optimistic: get current value and add
                    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
                    const collectionPath = this.getCollectionPath(normalizedPath);
                    const sub = this.findSubscriptionByPath(collectionPath);
                    const existing = sub === null || sub === void 0 ? void 0 : sub.docs.get(normalizedPath);
                    const current = (_a = existing === null || existing === void 0 ? void 0 : existing[key]) !== null && _a !== void 0 ? _a : 0;
                    resolved[key] = (typeof current === 'number' ? current : 0) + op.value;
                }
                else {
                    resolved[key] = value;
                }
            }
            else {
                resolved[key] = value;
            }
        }
        return resolved;
    }
    rejectAllPending(reason) {
        for (const [requestId, pending] of this.pendingRequests) {
            clearTimeout(pending.timeout);
            pending.reject(new Error(reason));
        }
        this.pendingRequests.clear();
    }
    setAllSubscriptionStatus(status) {
        for (const sub of this.subscriptions.values()) {
            sub.status = status;
            this.notifyState(sub);
        }
    }
    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------
    close() {
        this.closed = true;
        if (this.reconnectTimer)
            clearTimeout(this.reconnectTimer);
        if (this.idbFlushTimer)
            clearTimeout(this.idbFlushTimer);
        if (this.tokenRefreshTimer)
            clearInterval(this.tokenRefreshTimer);
        this.flushIdb();
        if (this.ws) {
            this.ws.close(1000, 'Store closed');
            this.ws = null;
        }
        this.rejectAllPending('Store closed');
        this.subscriptions.clear();
    }
    async reconnectWithNewAuth() {
        if (this.closed)
            return;
        await this.ensureInitialized();
        await this.refreshToken();
        await this.applyAuthPrincipalChange();
        if (this.subscriptions.size > 0) {
            await this.ensureConnected().catch((error) => {
                this.setAllSubscriptionStatus('error');
                for (const sub of this.subscriptions.values()) {
                    sub.error = error instanceof Error ? error : new Error(String(error));
                    this.notifyState(sub);
                }
            });
        }
    }
}
// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------
let storeInstance = null;
export function getRealtimeStore() {
    if (!storeInstance) {
        storeInstance = new RealtimeStore();
    }
    return storeInstance;
}
export function resetRealtimeStore() {
    if (storeInstance) {
        storeInstance.close();
        storeInstance = null;
    }
}
export async function reconnectRealtimeStoreWithNewAuth() {
    if (storeInstance) {
        await storeInstance.reconnectWithNewAuth();
    }
}
